import { z } from "zod";
import { PropertyId, Uuid } from "./primitives";
import { CampaignView, MAX_OFFERS_PER_CAMPAIGN, OWNER_OFFER_REWARD_CHOICES, OfferTerms, OfferVersionView } from "./rewards";

/**
 * "Add an offer" on one screen (ADR-0027, founder request 2026-10-20: "Also can we make it easier to "add offers" as a
 * building owner?"). The CURRENT owner of a store reads its offers with one plain status (GET /stores/:id/offers) and
 * saves them with ONE command (PUT /stores/:id/offers): the store's campaign (one per store and ownership interval,
 * ADR-0011) is created or edited and sent to review in one transaction; once it passes review, a campaign saved here
 * starts by itself (no Activate button).
 *
 * ADR-0029 (founder decision 2026-10-20): offers are FREE for the building owner. Nothing is paid and no budget exists
 * for them: Townsy gives the coins. ADR-0030: there is no per-store daily coin limit (only the per-visitor limits), and
 * an owner who lost a store can re-use their earlier offers on their next one (`reuse`). Nothing in these DTOs is
 * authority: the server re-checks ownership, the store's live design, every rule and every limit.
 */

/** What visitors do: the two kinds the one screen offers (partner-confirmed tasks stay on the advertiser page). */
export const StoreOfferKind = z.enum(["website_visit", "quiz"]);
export type StoreOfferKind = z.infer<typeof StoreOfferKind>;

/** ADR-0034: the coins an owner's offer gives: exactly one of these (no other amount can be typed). */
export const STORE_OFFER_REWARD_CHOICES = OWNER_OFFER_REWARD_CHOICES;
/**
 * The one plain status of a store's (free, ADR-0029) offers:
 *  - none: no offers yet for this ownership of the store;
 *  - draft: saved on the advertiser page, never sent to a reviewer;
 *  - checking: the automated check or a Townsy reviewer looks at new offers;
 *  - ready: passed review, but not running (`note` says why, e.g. a website link no longer accepted);
 *  - live: visitors can start them;
 *  - needs_change: a reviewer asked for a change (`note`);
 *  - paused: paused by the owner on the advertiser page;
 *  - stopped: paused by Townsy staff (`note`); edit and send again;
 *  - no_offers: every offer was removed (or ended by a website change without a new address).
 */
export const StoreOffersState = z.enum(["none", "draft", "checking", "ready", "live", "needs_change", "paused", "stopped", "no_offers"]);
export type StoreOffersState = z.infer<typeof StoreOffersState>;

export const StoreOffersStatus = z.object({
  state: StoreOffersState,
  /** needs_change / stopped: the reviewer's note; ready: why it is not running. Plain text, else null. */
  note: z.string().nullable(),
  /** Rewards claimed so far on this store's offers (this ownership of the store). */
  completions: z.number().int().min(0),
  /** Coins Townsy gave visitors for these offers so far (this ownership of the store). */
  coinsGiven: z.number().int().min(0),
  /** The offers start by themselves once they pass review (saved on this screen). */
  autoStart: z.boolean(),
});
export type StoreOffersStatus = z.infer<typeof StoreOffersStatus>;

/** Today's defaults per kind (config/economy.defaults.json `rewards`; ADR-0011 #3). Free offers have no price. */
export const StoreOfferDefaults = z.object({
  website_visit: z.object({
    rewardCoins: z.number().int(),
    attemptTtlSeconds: z.number().int(),
    minReturnSeconds: z.number().int(),
  }),
  quiz: z.object({
    rewardCoins: z.number().int(),
    attemptTtlSeconds: z.number().int(),
    maxTries: z.number().int(),
  }),
});
export type StoreOfferDefaults = z.infer<typeof StoreOfferDefaults>;

export const StoreOfferLimits = z.object({
  maxOffers: z.number().int(),
  maxRewardCoins: z.number().int(),
  rewardChoices: z.array(z.number().int()),
});
export type StoreOfferLimits = z.infer<typeof StoreOfferLimits>;

export const STORE_OFFER_LIMITS: StoreOfferLimits = {
  maxOffers: MAX_OFFERS_PER_CAMPAIGN,
  maxRewardCoins: Math.max(...STORE_OFFER_REWARD_CHOICES),
  rewardChoices: [...STORE_OFFER_REWARD_CHOICES],
};

/**
 * ADR-0030 (founder 2026-10-20: "... it can prompt them to "re-use same offers" to prevent them from having to set them
 * up again"): the owner's own earlier offers this store can re-use. Only ever the requesting account's OWN campaign
 * (never a previous owner's of this store). Source: the owner's most recent earlier campaign whose offers no longer run,
 * preferring one from a store they no longer own; its active website / question offers (latest versions; partner tasks
 * stay in the advertiser tools). Re-use is a prefill of this screen: the owner ticks the consent again and sends the new
 * offers to review (website offers then send visitors to THIS store's website).
 */
export const StoreOffersReuse = z.object({
  fromCampaignId: Uuid,
  fromPropertyId: PropertyId,
  fromAddress: z.string(),
  fromBusinessName: z.string(),
  /** The offers' latest terms (owner view, the right answer included; prices dropped: offers are free). */
  offers: z.array(OfferVersionView.shape.terms).min(1).max(MAX_OFFERS_PER_CAMPAIGN),
  /** Website offers among them that can't be re-used until this store has a live website (0 when it has one). */
  websiteOffersNeedWebsite: z.number().int().min(0),
});
export type StoreOffersReuse = z.infer<typeof StoreOffersReuse>;

/** GET /api/v1/stores/:propertyId/offers — the current owner's offers, one plain status, defaults and limits. */
export const StoreOffersView = z.object({
  propertyId: PropertyId,
  address: z.string(),
  /**
   * The store's live design, which offers are built on (ADR-0011): its business name and the website host "Visit my
   * website" sends visitors to. Null while the owner's design is not live on the store yet ("Set up your store" first).
   */
  store: z.object({ businessName: z.string(), description: z.string(), websiteHost: z.string().nullable() }).nullable(),
  /** The store's offers for this ownership (owner view, quiz answers included), or null when there are none yet. */
  campaign: CampaignView.nullable(),
  status: StoreOffersStatus,
  defaults: StoreOfferDefaults,
  limits: StoreOfferLimits,
  /** ADR-0028: the automated review decides clear cases on this server (usually within a minute); else a person. */
  quickCheck: z.boolean(),
  /**
   * ADR-0029: this ownership of the store also has offers paid from a budget before offers became free (advertiser
   * tools). They keep running until their budget is used; this screen shows and saves the free offers only.
   */
  paidOffersElsewhere: z.boolean(),
  /** ADR-0030: the owner's own earlier offers to re-use here, while this store has no running offers (else null). */
  reuse: StoreOffersReuse.nullable(),
});
export type StoreOffersView = z.infer<typeof StoreOffersView>;

/**
 * PUT /api/v1/stores/:propertyId/offers (current owner only; verified email; Idempotency-Key required). Entries with an
 * offerId replace that offer's terms (a NEW immutable version when they differ, which a reviewer checks again); entries
 * without one add an offer; offers not listed stay as they are. Kinds: website visit and quiz only. Every new or changed
 * offer carries the owner's explicit consent (REWARD_CONSENT_TEXT). The save sends the offers to review. Offers are free
 * (ADR-0029): any `billableCents` sent is ignored and stored as 0.
 */
export const StoreOffersRequest = z
  .object({
    /** The version of the store's offers the screen started from (null: the store had none). */
    expectedVersion: z.number().int().min(1).nullable(),
    offers: z
      .array(z.object({ offerId: Uuid.nullable(), terms: OfferTerms }).strict())
      .min(1)
      .max(MAX_OFFERS_PER_CAMPAIGN),
    /**
     * ADR-0030: the screen was prefilled from this earlier campaign of the SAME account ("Re-use your offers"). Audited
     * with the save; the server refuses a campaign that is not the account's own. The offers themselves are new.
     */
    reusedFromCampaignId: Uuid.nullable().optional(),
  })
  .strict();
export type StoreOffersRequest = z.infer<typeof StoreOffersRequest>;
export type StoreOffersRequestInput = z.input<typeof StoreOffersRequest>;

export const StoreOffersResponse = z.object({
  offers: StoreOffersView,
  saved: z.object({
    /** submitted: sent to a Townsy reviewer; unchanged: nothing new to review (the request matched what is saved). */
    result: z.enum(["submitted", "unchanged"]),
    campaignId: Uuid,
    /** The store had no offers before this save. */
    created: z.boolean(),
    /** New immutable offer versions written by this save (new offers count too). */
    newVersions: z.number().int().min(0),
  }),
});
export type StoreOffersResponse = z.infer<typeof StoreOffersResponse>;
